import type { MedplumClient } from "@medplum/core";
import type { Provenance } from "@medplum/fhirtypes";
import { Source } from "@/models/source/Source";

export interface ProvenanceSourceSet {
  documentReferenceId?: string;
  documentReferenceIds?: string[];
  surveyResponseId?: string;
  surveyResponseIds?: string[];
  references?: string[];
}

/**
 * Normalizes provenance source identifiers into FHIR reference strings and removes duplicates.
 */
export function buildProvenanceSources(
  ...sourceSets: ProvenanceSourceSet[]
): Array<{ reference: string }> {
  const references = new Set<string>();

  const addReference = (raw: string | undefined, resourceType?: string) => {
    if (!raw) {
      return;
    }

    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      return;
    }

    const normalized =
      resourceType && !trimmed.includes("/") ? `${resourceType}/${trimmed}` : trimmed;

    references.add(normalized);
  };

  for (const set of sourceSets) {
    if (!set) {
      continue;
    }

    addReference(set.documentReferenceId, "DocumentReference");
    addReference(set.surveyResponseId, "QuestionnaireResponse");

    for (const id of set.documentReferenceIds || []) {
      addReference(id, "DocumentReference");
    }

    for (const id of set.surveyResponseIds || []) {
      addReference(id, "QuestionnaireResponse");
    }

    for (const reference of set.references || []) {
      addReference(reference);
    }
  }

  return Array.from(references).map((reference) => ({ reference }));
}

/**
 * Searches for Provenance resources by target reference.
 */
export async function searchProvenance({
  medplum,
  targetRef,
  count = 1,
}: {
  medplum: MedplumClient;
  targetRef: string;
  count?: number;
}): Promise<Provenance[]> {
  const sources = await Source.search(
    medplum,
    { target: { reference: targetRef } },
    { limit: count },
  );
  return sources.map((source) => source.serialize());
}

/**
 * Creates a Provenance resource.
 */
export async function createProvenance({
  medplum,
  provenance,
}: {
  medplum: MedplumClient;
  provenance: Provenance;
}): Promise<Provenance> {
  const source = await Source.createFromProvenance(medplum, provenance);
  return source.serialize();
}

/**
 * Updates an existing Provenance resource.
 */
export async function updateProvenance({
  medplum,
  provenance,
}: {
  medplum: MedplumClient;
  provenance: Provenance;
}): Promise<Provenance> {
  const source = await Source.updateFromProvenance(medplum, provenance);
  return source.serialize();
}
